import { matchTasksToAppointments } from '../../domain/services/reconciliationMatcher';

const CONCURRENCY = 5;

/**
 * Creates a reconciliation runner that processes session activities concurrently.
 * @param {Function} loadPotentialAppointments - Calls the API to load potential appointments
 * for a single session activity
 * @param {Function} linkSessionActivityToAppointment - Calls the API to atomically link a session
 * activity to an appointment
 * @returns {{ prepare: Function, match: Function, commit: Function, cancel: Function }}
 */
// eslint-disable-next-line import/prefer-default-export
export function createReconciliationRunner(loadPotentialAppointments, linkSessionActivityToAppointment) {
  let tasks = [];
  let abortController = null;
  let isRunning = false;

  /**
   * Load the reconciliation tasks into the runner. Accepts pre-created task objects so callers
   * can wrap them in Vue's reactive() before passing them in — ensuring the runner's mutations
   * go through Vue's Proxy and trigger computed/template updates.
   * @param {import('../../domain/entities/ReconciliationTask.types').ReconciliationTask[]} providedTasks
   * @returns {void}
   */
  function prepare(providedTasks) {
    if (isRunning) {
      throw new Error('Cannot prepare tasks while reconciliation run is in progress');
    }
    tasks = providedTasks;
  }

  /**
   * Process a single task: mark processing, call the API, store result or error.
   * @param {import('../../domain/entities/ReconciliationTask.types').ReconciliationTask} task
   * @param {AbortSignal} signal
   * @returns {Promise<void>}
   */
  async function loadTask(task, signal) {
    task.markProcessing();
    try {
      const potentialAppointments = await loadPotentialAppointments({
        clinicId: task.sessionActivity.clinic,
        userId: task.sessionActivity.user,
        clientId: task.sessionActivity.client,
        date: task.sessionActivity.startTimestamp,
      }, { signal });
      task.markLoaded(potentialAppointments);
    } catch (err) {
      if (signal.aborted) { task.markCancelled(); return; }
      task.markFailed(err?.message ?? 'Unknown error');
    }
  }

  async function commitTask(task, signal) {
    task.markProcessing();
    try {
      if (signal.aborted) { task.markCancelled(); return; }
      await linkSessionActivityToAppointment(
        task.sessionActivity.id,
        task.selectedAppointmentId,
      );
      task.markLinked();
    } catch (err) {
      if (signal.aborted) { task.markCancelled(); return; }
      task.markFailed(err?.message ?? 'Unknown error');
    }
  }

  /**
   * Worker loop: repeatedly pull the next eligible task and process it.
   * Exits when no eligible tasks remain.
   * @param {Function} processFn - Async function to process a single task
   * @param {Function} taskPredicate - Predicate to select the next eligible task
   * @param {AbortSignal} signal
   * @param {Function} [onTaskComplete] - Optional callback invoked after each task completes
   * @returns {Promise<void>}
   */
  async function worker(processFn, taskPredicate, signal, onTaskComplete = null) {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (signal.aborted) {
        return;
      }
      const next = tasks.find(taskPredicate);
      if (!next) {
        return;
      }
      // eslint-disable-next-line no-await-in-loop
      await processFn(next, signal);
      if (typeof onTaskComplete === 'function') {
        onTaskComplete(next);
      }
    }
  } // worker()

  /**
   * Run the reconciliation process: start multiple workers to process tasks concurrently.
   * Prevents multiple simultaneous runs and ensures all tasks are processed before finishing.
   * @param {Function} processFn - Async function to process a single task
   * @param {Function} taskPredicate - Predicate to select eligible tasks
   * @param {Function} [onTaskComplete] - Optional callback invoked after each task completes
   * @returns {Promise<void>}
   */
  async function run(processFn, taskPredicate, onTaskComplete = null) {
    if (isRunning) {
      throw new Error('Reconciliation run already in progress');
    }
    isRunning = true;
    abortController = new AbortController();
    const { signal } = abortController;
    const workers = Array.from(
      { length: Math.min(CONCURRENCY, tasks.filter(taskPredicate).length) },
      () => worker(processFn, taskPredicate, signal, onTaskComplete),
    );
    try {
      await Promise.all(workers);
    } finally {
      isRunning = false;
      if (signal.aborted) {
        tasks.filter(taskPredicate).forEach((t) => t.markCancelled());
      }
    }
  } // run()

  /**
   * Match session activities to appointments using the given settings. This will load potential appointments
   * for each pending task, then greedily match loaded tasks to their best available appointments.
   * Prevents multiple simultaneous runs and ensures all tasks are processed before finishing.
   * @param {import('../../domain/entities/ReconciliationSettings.types').ReconciliationSettings} settings
   * @param {Function} [onTaskComplete] - Optional callback invoked after each task completes
   * @returns {Promise<void>}
   */
  async function match(settings, onTaskComplete = null) {
    await run(
      (task, signal) => loadTask(task, signal),
      (task) => task.isPending(),
      onTaskComplete,
    );
    if (abortController?.signal.aborted) {
      return;
    }
    matchTasksToAppointments(tasks, settings);
  } // match()

  /**
   * Commit the selected appointments for confirmed tasks. This will link session activities
   * to their respective appointments.
   * @param {Function} [onTaskComplete] - Optional callback invoked after each task completes
   * @returns {Promise<{linked: number, failed: number, cancelled: number}>}
   */
  async function commit(onTaskComplete = null) {
    await run(
      (task, signal) => commitTask(task, signal),
      (task) => task.isConfirmed(),
      onTaskComplete,
    );
    return {
      linked: tasks.filter((t) => t.isLinked()).length,
      failed: tasks.filter((t) => t.isFailed()).length,
      cancelled: tasks.filter((t) => t.isCancelled()).length,
    };
  } // commit()

  /**
   * Cancel the ongoing reconciliation process by aborting the workers. This will cause in-flight
   * API calls to fail and mark remaining tasks as canceled.
   * @returns {void}
   */
  function cancel() {
    abortController?.abort();
  } // cancel()

  return { prepare, match, commit, cancel };
}
